import { Instance, SnapshotOut, types } from 'mobx-state-tree'
import { Player } from '@ppwr/poker'
import { withRootStore } from '../extensions'

import AgoraRTC, {
  IAgoraRTCRemoteUser,
  IRemoteAudioTrack,
  IRemoteVideoTrack,
  IAgoraRTCClient,
  ICameraVideoTrack,
  IMicrophoneAudioTrack
} from 'agora-rtc-react'

import { rtcClient } from '../../../module/agora/agora.web'

const { createCameraVideoTrack, createMicrophoneAudioTrack } = AgoraRTC

export enum RTCRole {
  publisher = 1,
  subscriber = 1
}

const appId = '06997babc99845ef943d90ee04cb1a99'

/**
 * Agora User Store Model
 */
export const AgoraUserStoreModel = types
  .model('AgoraUserStoreModel')
  .props({
    uid: types.identifierNumber,
    videoMuteState: types.optional(types.boolean, false)
  })
  .volatile(() => ({
    rtcUser: null as IAgoraRTCRemoteUser | null,
    audioTrack: null as IRemoteAudioTrack | null,
    videoTrack: null as IRemoteVideoTrack | null
  }))
  .actions((self) => ({
    update(rtcUser: IAgoraRTCRemoteUser) {
      self.rtcUser = rtcUser
      self.audioTrack = rtcUser.audioTrack ?? null
      self.videoTrack = rtcUser.videoTrack ?? null
    }
  }))

export function createMyRemoteUser(rtcUser: IAgoraRTCRemoteUser) {
  const modelCreated = AgoraUserStoreModel.create({
    uid: Number(rtcUser.uid)
  }) // initialize volatile

  modelCreated.update(rtcUser)

  return modelCreated
}

export const PlayerAVStatusModel = types.model('PlayerAVStatusModel').props({
  userId: types.string,
  isPlayerOnAudioCall: types.boolean,
  isPlayerOnVideoCall: types.boolean,
  isPlayerMuted: types.boolean
})

export interface PlayerAVStatusType extends Instance<typeof PlayerAVStatusModel> {}

/**
 * Agora Store Model
 */
export const AgoraStoreModel = types
  .model('AgoraStoreModel')
  .props({
    agoraToken: types.optional(types.string, ''),
    agoraUsers: types.optional(types.array(AgoraUserStoreModel), []),
    isLocalUserJoined: types.optional(types.boolean, false),
    isLocalUserMuted: types.optional(types.boolean, false),
    isLocalUserVideoHidden: types.optional(types.boolean, true),
    showOptionModal: types.optional(types.boolean, false),
    isEveryoneMute: types.optional(types.boolean, false),
    usersAVCallStatus: types.optional(types.array(PlayerAVStatusModel), []),
    talkingPlayerId: types.optional(types.string, ''),
    userJoinedWithCamera: types.optional(types.boolean, false),
    userJoinedWithAudioVideoOff: types.optional(types.boolean, false),
    userTappedOnGoLive: types.optional(types.boolean, false),
    isMicOff: types.optional(types.boolean, false),
    isCamOff: types.optional(types.boolean, false)
  })
  .extend(withRootStore)
  .volatile((self) => {
    const rtcEngine = undefined
    return {
      rtcEngine
    } as { rtcEngine?: IAgoraRTCClient }
  })
  .volatile((self) => {
    const localCameraTrack = undefined
    return {
      localCameraTrack
    } as { localCameraTrack?: ICameraVideoTrack }
  })
  .volatile((self) => {
    const localMicTrack = undefined
    return {
      localMicTrack
    } as { localMicTrack?: IMicrophoneAudioTrack }
  })
  .views((self) => ({
    getIsPlayerOnAVCall(player: Player) {
      return player && self.agoraUsers.map((user) => user.uid).includes(Number(player.id))
    },
    getRemoteUser(playerId: number) {
      return self.agoraUsers.find((user) => user.uid === playerId)
    },
    playerAVStatus(player: Player): PlayerAVStatusType | undefined {
      //console.log('playerAVStatus', { player, usersAVCallStatus: self.usersAVCallStatus })
      const playerAVCallStatus = self.usersAVCallStatus.find(
        (playerAVStatus) => playerAVStatus.userId === player.userId
      )
      return playerAVCallStatus
    }
  }))
  .actions((self) => ({
    clearAgoraToken() {
      self.agoraToken = ''
    },
    setAgoraToken(token: string) {
      self.agoraToken = token
    },
    // setRTCEngine(engine: IAgoraRTCClient | undefined) {
    //   self.rtcEngine = engine
    // },
    setLocalCameraTrack(track: ICameraVideoTrack | undefined) {
      self.localCameraTrack = track
    },
    setLocalMicTrack(track: IMicrophoneAudioTrack | undefined) {
      self.localMicTrack = track
    },
    setLocalUserJoined(value: boolean) {
      self.isLocalUserJoined = value
    },
    setLocalUserMutedStatus(value: boolean) {
      self.isLocalUserMuted = value
    },
    setLocalUserVideoStatus(value: boolean) {
      self.isLocalUserVideoHidden = value
    },
    setShowOptionModal(value: boolean) {
      self.showOptionModal = value
    },
    setIsEveryoneMute(value: boolean) {
      self.isEveryoneMute = value
    },
    setUsersOnVideoCall(value: PlayerAVStatusType[]) {
      self.usersAVCallStatus.replace(value)
    },
    setUserTappedOnGoLive(value: boolean) {
      self.userTappedOnGoLive = value
    },
    setIsCamOff(value: boolean) {
      self.isCamOff = value
    },
    setIsMicOff(value: boolean) {
      self.isMicOff = value
    },
    setTalkingPlayerId(value: string) {
      self.talkingPlayerId = value
    },
    spliceAgoraUsers(
      index: number,
      deleteCount: number,
      user?: { uid: number; videoMuteState: boolean; rtcUser?: IAgoraRTCRemoteUser }
    ) {
      if (user) {
        // Adding User
        if (user.rtcUser) {
          const myRemoteUser = createMyRemoteUser(user.rtcUser)

          self.agoraUsers.splice(index, deleteCount, myRemoteUser)
        } else {
          self.agoraUsers.splice(index, deleteCount, user)
        }
      } else {
        // Removing User
        self.agoraUsers.splice(index, deleteCount)
      }
      //self.agoraUsers.push({ uid: Number(user?.uid), videoMuteState: user?.videoMuteState })
    },
    setUserJoinedWithCamera(value: boolean) {
      self.userJoinedWithCamera = value
    },
    setUserJoinedWithAudioVideoOff(value: boolean) {
      self.userJoinedWithAudioVideoOff = value
    }
  }))
  .actions((self) => ({
    setRemoteUserVideoStatus(value: number, state: boolean) {
      const tarIndex = self.agoraUsers.map((user) => user.uid).indexOf(value)
      if (tarIndex !== -1) {
        if (state) {
          // self.agoraUsers[tarIndex].videoMuteState = true
          self.spliceAgoraUsers(tarIndex, 1, { uid: value, videoMuteState: true })
        } else {
          // self.agoraUsers[tarIndex].videoMuteState = false
          self.spliceAgoraUsers(tarIndex, 1, { uid: value, videoMuteState: false })
        }
      } else {
        console.warn(`REMOTE VIDEO WARNING. TOGGLING USER NOT IN AGORAUSERS LIST.${value}`, state)
      }
    }
  }))
  .actions((self) => ({
    async getAgoraToken(channelName: string, uid: number, role: RTCRole = RTCRole.publisher): Promise<string> {
      console.log('session-agora getAgoraToken', { channelName, uid, role })
      try {
        if (!channelName) {
          console.error('Cannot get Agora token: channelName is empty')
          throw new Error('Channel name is required for token generation')
        }

        if (uid === undefined || uid === null) {
          console.error('Cannot get Agora token: uid is invalid', uid)
          throw new Error('Valid uid is required for token generation')
        }

        const response = await self.rootStore.api.queryGetAgoraToken({ channelName, uid, role })

        if (!response?.getAgoraToken) {
          console.error('Received invalid response from token API', response)
          throw new Error('Invalid token response from server')
        }

        console.log('Agora token received successfully', {
          tokenLength: response.getAgoraToken.length,
          channelName,
          uid
        })

        // Store the token in the state
        self.setAgoraToken(response.getAgoraToken)
        return response.getAgoraToken
      } catch (error) {
        console.error('Error in getting Agora token', error)
        throw error
      }
    }
  }))
  .actions((self) => ({
    async pauseVideoStream() {
      //self.rtcEngine?.muteLocalVideoStream(true)
      self.localCameraTrack?.setEnabled(false)
      self.setLocalUserVideoStatus(true)
    },
    async resumeVideoStream() {
      self.localCameraTrack?.setEnabled(true)
      //self.rtcEngine?.muteLocalVideoStream(false)
      self.setLocalUserVideoStatus(false)
    },
    async startVideoStream() {
      if (!self.userJoinedWithCamera) {
        const [camTrack] = await Promise.all([
          // create local track, using camera
          createCameraVideoTrack()
        ])
        self.setLocalCameraTrack(camTrack)
        // publish local tracks to channel
        await rtcClient.publish(camTrack)
        self.rootStore.communication.setUserTappedOnGoLive(true)
      }
      self.localCameraTrack?.setEnabled(true)
      //self.rtcEngine?.muteLocalVideoStream(false)
      self.setLocalUserVideoStatus(false)
    },
    async startAudioStream() {
      if (self.userJoinedWithAudioVideoOff) {
        const track = await createMicrophoneAudioTrack({
          AEC: true,
          ANS: true
        })
        await self.rtcEngine?.publish(track)
        await rtcClient.publish(track)
        self.setLocalMicTrack(track)
        self.setUserJoinedWithAudioVideoOff(false)
      }
    },
    async muteVideoCall() {
      //self.rtcEngine?.muteLocalAudioStream(true)
      self.localMicTrack?.setEnabled(false)
      self.setLocalUserMutedStatus(true)
      self.setTalkingPlayerId('')
    },
    async unMuteVideoCall() {
      //self.rtcEngine?.muteLocalAudioStream(false)
      self.localMicTrack?.setEnabled(true)
      self.setLocalUserMutedStatus(false)
    },
    async muteEveryone() {
      // await self.rtcEngine?.muteAllRemoteAudioStreams(true)
      self.isEveryoneMute = true
    },
    async unmuteEveryone() {
      // await self.rtcEngine?.muteAllRemoteAudioStreams(false)
      self.isEveryoneMute = false
    },
    async setPlayerVideoCallStatus(isPlayerOnVideoCall: boolean, isPlayerOnAudioCall: boolean, isPlayerMuted: boolean) {
      try {
        const userId = self.rootStore.session.user?._id.toString()
        const channelName = self.rootStore.communication.channel?.channelName

        if (userId && channelName) {
          const isFlagEnabled = true //checkFeatureFlag('set_player_video_call_Status')
          if (isFlagEnabled) {
            await self.rootStore.api.mutateSetPlayerVideoCallStatus({
              channelName,
              isPlayerOnVideoCall,
              isPlayerOnAudioCall,
              isPlayerMuted,
              userId
            })
            self.setLocalUserMutedStatus(isPlayerMuted)
          }
        }
      } catch (error) {
        console.log('mutateSetPlayerVideoCallStatus error: ', error)
      }
    }
  }))
  .actions((self) => ({
    async hideVideoAndMuteCallLocalUser() {
      self.setPlayerVideoCallStatus(false, true, true)
      await self.pauseVideoStream()
      await self.muteVideoCall()
    },
    async restoreVideoAnMuteForLocalUser() {
      if (!self.isCamOff) {
        self.resumeVideoStream()
      }

      if (!self.isMicOff) {
        self.unMuteVideoCall()
      }

      self.setPlayerVideoCallStatus(self.isCamOff, true, self.isMicOff)
    },
    async showVideoLocalUser() {
      self.setPlayerVideoCallStatus(true, true, self.isLocalUserMuted)
      await self.resumeVideoStream()
    },
    async subscribeToRemoteUsers() {
      // if (!self.rtcEngine) {
      //   console.log('Cannot subscribe to remote users: RTC engine not initialized')
      //   return
      // }
      try {
        // Get all remote users from the Agora client
        const remoteUsers = rtcClient.remoteUsers
        console.log('yara Manually subscribing to remote users:', remoteUsers)
        // Subscribe to each remote user's tracks
        remoteUsers.forEach(async (user) => {
          const remoteUid = Number(user.uid) ?? 0
          console.log(`Manually subscribe user ${remoteUid}`)
          try {
            // Subscribe to video if available
            if (user.hasVideo) {
              await rtcClient.subscribe(user, 'video')
              console.log('Manually subscribed to video for user:', remoteUid)
            }
            // Subscribe to audio if available
            // if (user.hasAudio) {
            //   await self.rtcEngine.subscribe(user, 'audio')
            //   console.log('Manually subscribed to audio for user:', remoteUid)
            // }
            // Update the user in our store
            // const index = self.agoraUsers.map((u) => u.uid).indexOf(remoteUid)
            // if (index !== -1) {
            //   self.spliceAgoraUsers(index, 1, {
            //     uid: remoteUid,
            //     videoMuteState: !user.hasVideo,
            //     rtcUser: user
            //   })
            // } else {
            //   self.spliceAgoraUsers(self.agoraUsers.length, 0, {
            //     uid: remoteUid,
            //     videoMuteState: !user.hasVideo,
            //     rtcUser: user
            //   })
            // }
          } catch (error) {
            console.error('Error subscribing to user:', remoteUid, error)
          }
        })
      } catch (error) {
        console.error('Error in subscribeToRemoteUsers:', error)
      }
    },

    async onJoinChannelSuccess(uid: number) {
      const index = self.agoraUsers.map((user) => user.uid).indexOf(uid)
      if (index === -1) {
        // We're not in the list yet.
        self.spliceAgoraUsers(uid, 0, { uid, videoMuteState: true })
      } else {
        // We're already in the list. Remove old entry, add the new one.
        self.spliceAgoraUsers(index, 1, { uid, videoMuteState: true })
      }
      self.setLocalUserJoined(true)

      if (self.userJoinedWithCamera) {
        const [micTrack, camTrack] = await Promise.all([
          // create local tracks, using microphone and camera
          createMicrophoneAudioTrack(),
          createCameraVideoTrack()
        ])

        self.setLocalMicTrack(micTrack)
        self.setLocalCameraTrack(camTrack)

        // publish local tracks to channel
        await rtcClient.publish([micTrack, camTrack])

        self.setPlayerVideoCallStatus(true, true, self.isLocalUserMuted)
        self.resumeVideoStream()
        self.rootStore.communication.setUserTappedOnGoLive(true)
      } else {
        if (!self.isLocalUserMuted) {
          const track = await createMicrophoneAudioTrack({
            AEC: true,
            ANS: true
          })
          await self.rtcEngine?.publish(track)
          await rtcClient.publish(track)
          self.setLocalMicTrack(track)
        }
        self.setPlayerVideoCallStatus(false, true, self.isLocalUserMuted)
        if (self.isLocalUserMuted) {
          self.muteVideoCall()
        }
        self.pauseVideoStream()
      }

      // Try to subscribe to any existing remote users
      await this.subscribeToRemoteUsers()
    },
    async setupVideo({ hasCamPermission, hasMicPermission }: { hasCamPermission: boolean; hasMicPermission: boolean }) {
      //if (!self.rtcEngine) {
      //const engine = AgoraRTC.createClient({ mode: 'rtc', codec: 'vp8' })
      //self.setRTCEngine(rtcClient)
      //rtcClient = engine

      // if permissions are disabled
      // or player is an observer (and not the teacher observer seat)
      const amIObserverAndNotSeated =
        self.rootStore.game.isMyPlayerObserver &&
        self.rootStore.game.seatedObserver?.id !== self.rootStore.game.myPlayer?.id
      //User joining the call without camera can be in audience mode.
      const isAudience =
        !(hasCamPermission || hasMicPermission) || amIObserverAndNotSeated || !self.userJoinedWithCamera

      if (isAudience) {
        if (self.isLocalUserMuted) {
          rtcClient?.setClientRole('audience')
        } else {
          rtcClient?.setClientRole('host')
        }
        //engine?.enableLocalVideo(false)
        // engine?.muteAllRemoteVideoStreams(false)
        // engine?.enableVideo()
      } else {
        if (hasCamPermission || self.userJoinedWithCamera) {
          //engine?.enableVideo()

          self.resumeVideoStream()
        }
        rtcClient?.setClientRole('host')
        // https://docs.agora.io/en/Video/API%20Reference/react_native/classes/beautyoptions.html

        //engine.muteLocalAudioStream(self.isLocalUserMuted)
        //engine.muteLocalVideoStream(self.isLocalUserVideoHidden)
      }

      //TODO: check audience mode
    },
    //
    async startCall({
      uid,
      withCamera,
      muted,
      hasCamPermission,
      hasMicPermission
    }: {
      uid: number
      withCamera: boolean
      muted: boolean
      hasCamPermission: boolean
      hasMicPermission: boolean
    }) {
      try {
        self.setLocalUserMutedStatus(muted)
        self.setUserJoinedWithCamera(withCamera)
        self.setUserJoinedWithAudioVideoOff(muted)

        const channelName = self.rootStore.communication.channel?.channelName
        const userId = self.rootStore.session.user?._id

        if (!(channelName && userId)) {
          console.error('Cannot start call: missing channelName or userId', { channelName, userId })
          return
        }

        // Get a fresh token for this session
        let token: string

        try {
          token = await self.getAgoraToken(channelName, uid)
          console.log('Agora token received:', token ? 'Token received successfully' : 'No token received')
          console.log({ token })
          if (!token) {
            console.error('Failed to get a valid Agora token')
            return
          }
        } catch (tokenError) {
          console.error('Error getting Agora token:', tokenError)
          return
        }

        console.log('Joining Agora channel with:', {
          appId: appId ? 'App ID exists' : 'No App ID',
          channelName,
          tokenLength: token ? token.length : 0,
          uid: uid || null
        })

        const onUserPublish = async (user: IAgoraRTCRemoteUser, mediaType: 'video' | 'audio') => {
          try {
            console.log(`here Subscribing to ${mediaType}`)
            await rtcClient.subscribe(user, mediaType)
            console.log(`here ${mediaType} subscribed`)

            const uid = Number(user.uid)

            const index = self.agoraUsers.findIndex((u) => u.uid === uid)
            const userInfo = { uid, videoMuteState: !user.hasVideo, rtcUser: user }

            if (index === -1) {
              self.spliceAgoraUsers(self.agoraUsers.length, 0, userInfo)
            } else {
              self.spliceAgoraUsers(index, 1, userInfo)
            }
          } catch (err) {
            console.error(err)
          }
        }

        try {
          const res = await rtcClient.join(appId, channelName, token, uid)

          console.log('Successfully joined Agora channel:', res)
          if (res) {
            await this.onJoinChannelSuccess(uid)
          }

          rtcClient.on('user-published', onUserPublish)

          rtcClient.on('user-unpublished', (user) => {
            console.log('here unpublished')
            const id = user.uid
            //delete remoteUsers[id]
            //$(`#player-wrapper-${id}`).remove()
          })

          rtcClient.on('user-left', (user) => {
            console.log('UserLeft', { user })
            const remoteUid = Number(user.uid) ?? 0
            //Get the index of uid to be deleted/removed
            const index = self.agoraUsers.map((user) => user.uid).indexOf(remoteUid)
            if (index !== -1) {
              self.spliceAgoraUsers(index, 1)
            }
          })

          rtcClient.on('user-joined', async (user) => {
            //TODO: probably we can remove this method, since publish is taking care of it as well, this is used when remote user join the channel
            console.log('UserJoined', { user })
          })
        } catch (error: unknown) {
          console.error('Failed to join Agora channel:', error)
          // Check if token is the issue
          if (error instanceof Error) {
            const errorMessage = error.message || error.toString()
            if (errorMessage.includes('token') || errorMessage.includes('authorized')) {
              console.error('Token validation failed. Token may be invalid, expired, or mismatched with channel/uid')
            }
          } else if (typeof error === 'object' && error !== null) {
            console.error('Unknown error object:', error)
          }
        }
      } catch (error) {
        console.error('Error in startCall:', error)
      }
    },
    async endCall() {
      try {
        rtcClient?.removeAllListeners()
        rtcClient?.leave()
        self.localCameraTrack?.close()
        self.localMicTrack?.close()
        self.setLocalCameraTrack(undefined)
        self.setLocalMicTrack(undefined)
        //rtcClient.leave()
        //self.rtcEngine?.leaveChannel()
        //self.rtcEngine?.removeAllListeners()
        //self.rtcEngine?.release()
        // const client = self.rtcEngine
        // if (client) {
        //   await client.leave()
        // }
        //self.setRTCEngine(undefined)
      } catch (error) {
        // add logger?
      }

      self.setTalkingPlayerId('')
      self.setLocalUserVideoStatus(true)
      self.setLocalUserMutedStatus(false)
      self.spliceAgoraUsers(0, self.agoraUsers.length)
      self.setLocalUserJoined(false)
    }
  }))
  .actions((self) => {
    const destroy = async () => {
      rtcClient?.removeAllListeners()
      rtcClient?.leave()
      //const client = self.rtcEngine
      //self.rtcEngine?.release()
      // if (client) {
      //   await client.leave()
      // }
    }

    const afterAttach = async () => {
      self.setShowOptionModal(false)
      self.setLocalUserJoined(false)
      self.setUserJoinedWithCamera(false)
      self.setUserJoinedWithAudioVideoOff(false)
      self.setUsersOnVideoCall([])
      self.setTalkingPlayerId('')
      self.setPlayerVideoCallStatus(false, false, false)
      // destroy()
      self.agoraUsers.replace([])
      console.log('session-agora afterAttach')
    }

    const beforeDestroy = async () => {
      console.log('session-agora beforeDestroy')
      await destroy()
    }

    return { afterAttach, beforeDestroy }
  })

/**
 * The AgoraStore instance.
 */
export type AgoraStoreType = Instance<typeof AgoraStoreModel>

/**
 * The data of a AgoraStore.
 */
export type AgoraStoreSnapshot = SnapshotOut<typeof AgoraStoreModel>
